/**
 * routes/Positions — the EX.8 open-position MANAGEMENT view (Exit Intelligence). For every
 * position the desk is (or recently was) managing it answers, at a glance: how much is at
 * risk right now (live NET R, uPnL), where the protective stop sits and HOW it has trailed
 * (the mark-vs-stop mini-chart), how far the winner ran and gave back (peak-MFE + giveback,
 * MAE/MFE), how long it has been open, how much has been banked via scale-outs, and — the
 * point of the layer — the LLM committee's HOLD / TIGHTEN / EXIT decisions with their
 * reasoning over time.
 *
 * It joins two reads: the live open positions from the shell feed (`/api/positions`) and the
 * persisted committee reviews (`/api/position-reviews`, EX.5). A symbol that is live shows as
 * OPEN; a symbol with reviews but no live position shows its managed history. Everything is
 * sourced — the page renders nothing it cannot read, and never fabricates a decision.
 *
 * Read-only and honest to the invariants (plan §1): exit management may only ever REDUCE
 * risk. Until the G0-EXIT drills pass the layer runs in SHADOW (computes + logs intents,
 * touches no live orders) — the page says so rather than implying the stop actually moved.
 */
import { useMemo, useState } from 'react';

import { api } from '../api/client';
import type {
  LivePosition,
  PositionReviewAction,
  PositionReviewRecord,
  PositionsResponse,
} from '../api/types';
import { usePoll } from '../api/usePoll';
import { useShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { TrailChart, type TrailPoint } from '../components/charts/TrailChart';
import { ago, num, pct, signedMoney } from '../lib/format';

const ACTION_BADGE: Record<PositionReviewAction, string> = {
  HOLD: 'badge--neutral',
  TIGHTEN_STOP: 'badge--warning',
  EXIT_NOW: 'badge--reject',
};
const ACTION_LABEL: Record<PositionReviewAction, string> = {
  HOLD: 'HOLD',
  TIGHTEN_STOP: 'TIGHTEN',
  EXIT_NOW: 'EXIT',
};

/** ms → compact "2h 15m" / "45m" / "30s" duration. */
function dur(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

/** Signed R, e.g. +1.62R / −1.00R (real minus glyph). */
function signedR(r: number): string {
  return `${r >= 0 ? '+' : '−'}${Math.abs(r).toFixed(2)}R`;
}

/** One symbol the desk is managing: its live position (if open) + its review history. */
interface Managed {
  readonly symbol: string;
  readonly live: LivePosition | null;
  /** Reviews newest-first. */
  readonly reviews: readonly PositionReviewRecord[];
}

function groupManaged(
  positions: readonly LivePosition[],
  reviews: readonly PositionReviewRecord[],
): Managed[] {
  const bySymbol = new Map<string, Managed>();
  const liveBy = new Map(positions.map((p) => [p.symbol, p]));
  const order: string[] = [];

  // Live positions lead (open work first), then any symbol that only has review history.
  for (const p of positions) {
    bySymbol.set(p.symbol, { symbol: p.symbol, live: p, reviews: [] });
    order.push(p.symbol);
  }
  const revBySymbol = new Map<string, PositionReviewRecord[]>();
  for (const r of reviews) {
    const list = revBySymbol.get(r.symbol) ?? [];
    list.push(r);
    revBySymbol.set(r.symbol, list);
  }
  for (const [symbol, list] of revBySymbol) {
    const existing = bySymbol.get(symbol);
    if (existing) {
      bySymbol.set(symbol, { ...existing, reviews: list });
    } else {
      bySymbol.set(symbol, { symbol, live: liveBy.get(symbol) ?? null, reviews: list });
      order.push(symbol);
    }
  }
  return order.map((s) => bySymbol.get(s)!);
}

/** Build the mark/stop trail from the review snapshots (oldest→newest) + the open seed. */
function trailPoints(m: Managed): TrailPoint[] {
  const chron = [...m.reviews].reverse(); // reviews arrive newest-first
  const pts: TrailPoint[] = [];
  const first = chron[0]?.lifecycle;
  if (first?.openedAt != null && first.entryPrice != null) {
    pts.push({ at: first.openedAt, mark: first.entryPrice, stop: null });
  }
  for (const r of chron) {
    const lc = r.lifecycle;
    if (!lc) continue;
    pts.push({ at: Date.parse(r.createdAt), mark: lc.markPrice, stop: lc.stopPrice });
  }
  return pts;
}

export function Positions(): JSX.Element {
  const live = useShell();
  const reviewsPoll = usePoll<{ reviews: readonly PositionReviewRecord[] }>(
    (s) => api.positionReviews(s),
    { intervalMs: 20_000 },
  );
  const positionsFeed: PositionsResponse | null = live.positions;
  const positions = positionsFeed?.positions ?? [];
  const reviews = reviewsPoll.data?.reviews ?? [];
  const source = live.overview?.source ?? positionsFeed?.source;

  const managed = useMemo(() => groupManaged(positions, reviews), [positions, reviews]);
  const shadow = reviews.some((r) => (r.bridgeStatus ?? '').toUpperCase().includes('SHADOW'));

  const firstPaint = !positionsFeed && !reviewsPoll.data && live.transport === 'connecting';
  if (firstPaint) {
    return (
      <>
        <PageHead updatedAt={live.updatedAt} source={source} />
        <div className="state"><span className="spinner" aria-hidden /><span>Connecting to terminal…</span></div>
      </>
    );
  }

  const openCount = positions.length;

  return (
    <>
      <PageHead updatedAt={live.updatedAt} source={source} />

      <div className="banner-note section" role="note">
        <strong>Exit management only ever reduces risk.</strong> The committee can tighten the stop, bank a
        partial, or exit — never widen a stop or add size (invariant §1). A deterministic ratchet keeps trailing
        even if the LLM is slow or errors.{' '}
        {shadow && (
          <span>Currently running in <strong>SHADOW</strong> — intents are computed and logged, live orders are not
          modified, until the G0-EXIT drills pass.</span>
        )}
      </div>

      {managed.length === 0 ? (
        <Card title="No managed positions">
          <div className="state">
            <span className="muted">
              {reviewsPoll.loading && !reviewsPoll.data
                ? 'Loading position reviews…'
                : 'The desk is flat — no open positions and no committee reviews yet. Managed positions appear here as soon as a bracket opens.'}
            </span>
          </div>
        </Card>
      ) : (
        <>
          <div className="pm-summary section">
            <span className="pm-summary__stat"><strong className="num">{openCount}</strong> open</span>
            <span className="pm-summary__stat"><strong className="num">{managed.length}</strong> managed</span>
            <span className="pm-summary__stat"><strong className="num">{reviews.length}</strong> committee review{reviews.length === 1 ? '' : 's'}</span>
          </div>
          {managed.map((m) => (
            <ManagedCard key={m.symbol} m={m} />
          ))}
        </>
      )}

      {reviewsPoll.error && (
        <div className="banner-note section" role="status">
          <span className="muted">Review feed unavailable: {reviewsPoll.error}. Live position data is still shown above.</span>
        </div>
      )}
    </>
  );
}

function ManagedCard({ m }: { m: Managed }): JSX.Element {
  const latest = m.reviews[0] ?? null;
  const lc = latest?.lifecycle ?? null;
  const p = m.live;
  const isOpen = p != null;

  // Freshest figures: prefer the live feed for price/uPnL, the latest review for the
  // exit-specific excursion/R/scale-out numbers it captured.
  const side = p?.side ?? (latest?.side ?? 'LONG');
  const entry = p?.entryPrice ?? lc?.entryPrice ?? null;
  const mark = p?.markPrice ?? lc?.markPrice ?? null;
  const stop = p?.stopPrice ?? lc?.stopPrice ?? null;
  const slArmed = p ? p.slArmed : stop != null;
  const uPnl = p?.uPnl ?? lc?.unrealizedPnl ?? null;
  const uPnlPct = p?.uPnlPct ?? null;
  const netR = latest?.unrealizedR ?? lc?.unrealizedR ?? null;
  const openedAt = p?.openedAt ?? lc?.openedAt ?? null;
  const timeInTrade = lc?.timeInTradeMs ?? (openedAt != null ? Date.now() - openedAt : null);
  const scaleOuts = lc?.scaleOutCount ?? 0;

  const trail = useMemo(() => trailPoints(m), [m]);

  return (
    <Card
      title={
        <span className="pm-title">
          <span className="pm-title__sym">{m.symbol}</span>
          <span className={`side side--${side.toLowerCase()}`}>{side}</span>
          {p != null && <span className="muted pm-title__lev">{p.leverage}×</span>}
          <span className={`badge ${isOpen ? 'badge--profit' : 'badge--neutral'} pm-title__status`}>
            {isOpen ? 'OPEN' : 'CLOSED'}
          </span>
          {(p?.seed ?? false) && <span className="badge badge--neutral">demo seed</span>}
        </span>
      }
      hint={
        latest ? (
          <span className={`badge ${ACTION_BADGE[latest.action]}`} title={`Committee: ${latest.action}`}>
            {ACTION_LABEL[latest.action]}
            {latest.confidence != null ? ` · ${latest.confidence}%` : ''}
          </span>
        ) : (
          <span className="muted">no review yet</span>
        )
      }
    >
      {/* ── the numbers ─────────────────────────────────────────────────────── */}
      <div className="pm-stats">
        <Metric label="Net R" value={netR != null ? signedR(netR) : '—'} tone={netR == null ? '' : netR >= 0 ? 'profit' : 'loss'} sub={latest ? `at review ${ago(Date.parse(latest.createdAt))}` : undefined} />
        <Metric
          label="Unrealised P/L"
          value={uPnl != null ? signedMoney(uPnl) : '—'}
          tone={uPnl == null ? '' : uPnl >= 0 ? 'profit' : 'loss'}
          sub={uPnlPct != null ? pct(uPnlPct) : undefined}
        />
        <Metric label="Entry → Mark" value={<>{entry != null ? num(entry) : '—'} <span className="muted">→</span> {mark != null ? num(mark) : '—'}</>} />
        <Metric
          label="Protective stop"
          value={stop != null ? num(stop) : '—'}
          tone={slArmed ? '' : 'loss'}
          sub={slArmed ? <span className="pm-armed"><span aria-hidden>✓</span> armed</span> : <span className="pm-naked"><span aria-hidden>⚠</span> none</span>}
        />
        <Metric
          label="Peak MFE"
          value={lc?.peakMfeR != null ? `+${lc.peakMfeR.toFixed(2)}R` : '—'}
          sub={lc?.givebackFromPeak != null ? `giveback ${num(lc.givebackFromPeak)}` : undefined}
        />
        <Metric
          label="MAE / MFE"
          value={<>{lc?.maeR != null ? `−${Math.abs(lc.maeR).toFixed(2)}` : '—'} <span className="muted">/</span> {lc?.mfeR != null ? `+${lc.mfeR.toFixed(2)}` : '—'}<span className="muted"> R</span></>}
        />
        <Metric
          label="Scale-out"
          value={`${scaleOuts} leg${scaleOuts === 1 ? '' : 's'}`}
          sub={lc?.realizedR != null && lc.realizedR !== 0 ? `banked ${signedR(lc.realizedR)}` : 'none banked'}
        />
        <Metric label="Time in trade" value={timeInTrade != null ? dur(timeInTrade) : '—'} sub={openedAt != null ? `opened ${ago(openedAt)}` : undefined} />
      </div>

      {/* ── how the stop is trailing ────────────────────────────────────────── */}
      {trail.length >= 2 ? (
        <div className="pm-chart">
          <TrailChart points={trail} entryPrice={entry} caption={`${m.symbol} mark vs protective stop over the review timeline`} />
        </div>
      ) : (
        <p className="pm-note muted">
          {m.reviews.length === 0
            ? 'No committee review yet — the position is managed on each 15m candle close and on an adverse-move interrupt.'
            : 'Not enough review history to plot the stop trail yet.'}
        </p>
      )}

      {/* ── committee decision timeline ─────────────────────────────────────── */}
      {m.reviews.length > 0 && (
        <div className="pm-reviews">
          <div className="pm-reviews__head muted">Committee decisions · newest first</div>
          <ul className="pm-review-list" role="list">
            {m.reviews.map((r) => (
              <ReviewItem key={r.id} r={r} />
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function Metric({
  label,
  value,
  sub,
  tone = '',
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: 'profit' | 'loss' | '';
}): JSX.Element {
  return (
    <div className="pm-metric">
      <span className="pm-metric__label">{label}</span>
      <span className={`pm-metric__value num ${tone}`}>{value}</span>
      {sub != null && <span className="pm-metric__sub muted">{sub}</span>}
    </div>
  );
}

function ReviewItem({ r }: { r: PositionReviewRecord }): JSX.Element {
  const [open, setOpen] = useState(false);
  const hasReason = r.reasoning != null && r.reasoning.trim().length > 0;
  const stopMove =
    r.action === 'TIGHTEN_STOP' && r.fromStop != null && r.toStop != null
      ? `${num(r.fromStop)} → ${num(r.toStop)}`
      : null;

  return (
    <li className={`pm-review${open ? ' pm-review--open' : ''}`}>
      <button
        type="button"
        className="pm-review__summary"
        aria-expanded={open}
        disabled={!hasReason}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`badge ${ACTION_BADGE[r.action]}`}>{ACTION_LABEL[r.action]}</span>
        <span className="pm-review__body">
          <span className="pm-review__meta secondary">
            {r.confidence != null ? `${r.confidence}% confidence` : 'no confidence'}
            {r.unrealizedR != null ? ` · ${signedR(r.unrealizedR)}` : ''}
            {stopMove ? ` · stop ${stopMove}` : ''}
            {r.bridgeStatus ? ` · ${r.bridgeStatus}` : ''}
          </span>
          {hasReason && !open && <span className="pm-review__preview muted">{r.reasoning}</span>}
        </span>
        <span className="pm-review__time muted num">{ago(Date.parse(r.createdAt))}</span>
        <span className="pm-review__chevron" aria-hidden>{hasReason ? (open ? '▾' : '▸') : '·'}</span>
      </button>
      {open && hasReason && <p className="pm-review__detail">{r.reasoning}</p>}
    </li>
  );
}

function PageHead({ updatedAt, source }: { updatedAt: number | null; source: string | undefined }): JSX.Element {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-head__title">Positions</h1>
        <span className="page-head__sub">
          {updatedAt ? `updated ${ago(updatedAt)}` : 'connecting…'}
          {source === 'demo-seed' && <span className="badge badge--neutral" style={{ marginLeft: 8 }}>demo seed</span>}
        </span>
      </div>
    </div>
  );
}
